export type SourceType = 'paste' | 'link' | 'upload';

// Lifecycle status
export type IngestionStatus = 
  | 'captured'      // Just ingested, no processing yet
  | 'pending'       // Queued for processing
  | 'processing'    // Currently being processed
  | 'review'        // Processed, awaiting admin review
  | 'processed'     // Ready (not yet DB-ingested)
  | 'failed';       // Processing failed

// Data type of information
export type IngestionDataType = 
  | 'provider'      // Provider-level info (org name, contact, etc.)
  | 'program'       // Program-specific info (single program details)
  | 'schedule'      // Program schedules / session times
  | 'calendar'      // Provider-wide calendar (holidays, closures)
  | 'category'      // Category-level info (e.g., "all STEM programs")
  | 'other';        // Catch-all

// For link sources: refresh expectation
export type RefreshHint = 
  | 'static'        // Content unlikely to change
  | 'periodic'      // May need periodic refresh (schedules, calendars)
  | 'manual'        // Admin will manually trigger refresh when needed
  | 'unknown';      // Not yet determined

export interface IngestionSource {
  source_type: SourceType;
  content?: string;      // For paste mode
  url?: string;          // For link mode
  file_name?: string;    // For upload mode
  file_size?: number;    // For upload mode
  timestamp: string;     // ISO timestamp when captured
  refresh_hint?: RefreshHint; // Only relevant for 'link' type
}

export interface ScrapeOptions {
  formats: ('markdown' | 'html')[];
  onlyMainContent: boolean;
  waitFor?: number;
}

export interface IngestionDraft {
  source: IngestionSource;
  raw_content: string;                    // Canonical scraped markdown (or pasted/uploaded text)
  raw_html?: string;                      // Optional HTML (for future debugging)
  scraped_at?: string;                    // ISO timestamp of last scrape
  scrape_error?: string;                  // Error message if scrape failed
  scrape_options?: ScrapeOptions;         // Effective options used for scrape (for re-scrape consistency)
  status: IngestionStatus;                // Lifecycle status
  data_type: IngestionDataType | null;    // Admin-selected data type (null = not yet set)
  structured_extraction: null;            // Placeholder for future
  ai_notes: null;                         // Placeholder for future
}

// Review status for admin workflow
export type ReviewStatus = 'pending' | 'approved' | 'rejected';

// Database record for raw_ingestions table
export interface RawIngestion {
  id: string;
  source_type: SourceType;
  source_url: string | null;
  source_file_name: string | null;
  source_file_size: number | null;
  refresh_hint: RefreshHint | null;
  raw_content: string;
  raw_html: string | null;
  content_hash: string | null;
  scrape_options: ScrapeOptions | null;
  status: IngestionStatus;
  data_type: IngestionDataType | null;
  scrape_error: string | null;
  scraped_at: string | null;
  structured_extraction: Record<string, unknown> | null;
  ai_notes: Record<string, unknown> | null;
  review_status: ReviewStatus | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

// Event log record for raw_ingestion_events table
export interface RawIngestionEvent {
  id: string;
  ingestion_id: string;
  event_type: IngestionEventType;
  payload: Record<string, unknown> | null;
  created_at: string;
  created_by: string | null;
}

// Event types for audit logging
export type IngestionEventType =
  | 'created'
  | 'scrape_started'
  | 'scrape_completed'
  | 'scrape_failed'
  | 'status_changed'
  | 'data_type_changed'
  | 'review_status_changed'
  | 'extraction_started'
  | 'extraction_completed'
  | 'extraction_failed'
  | 'ai_notes_updated'
  | 'content_updated'
  | 'deleted';

// Helper to compute content hash (SHA-256)
export async function computeContentHash(content: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(content);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// Helper to convert IngestionDraft to database insert payload
export async function draftToInsertPayload(
  draft: IngestionDraft,
  userId: string
): Promise<Omit<RawIngestion, 'id' | 'created_at' | 'updated_at' | 'reviewed_at' | 'reviewed_by' | 'structured_extraction' | 'ai_notes' | 'review_status'>> {
  const contentHash = await computeContentHash(draft.raw_content);
  
  return {
    source_type: draft.source.source_type,
    source_url: draft.source.url ?? null,
    source_file_name: draft.source.file_name ?? null,
    source_file_size: draft.source.file_size ?? null,
    refresh_hint: draft.source.refresh_hint ?? null,
    raw_content: draft.raw_content,
    raw_html: draft.raw_html ?? null,
    content_hash: contentHash,
    scrape_options: draft.scrape_options ?? null,
    status: draft.status,
    data_type: draft.data_type,
    scrape_error: draft.scrape_error ?? null,
    scraped_at: draft.scraped_at ?? null,
    created_by: userId,
  };
}

// Helper to convert RawIngestion back to IngestionDraft for editing
export function ingestionToDraft(ingestion: RawIngestion): IngestionDraft {
  return {
    source: {
      source_type: ingestion.source_type,
      url: ingestion.source_url ?? undefined,
      file_name: ingestion.source_file_name ?? undefined,
      file_size: ingestion.source_file_size ?? undefined,
      timestamp: ingestion.created_at,
      refresh_hint: ingestion.refresh_hint ?? undefined,
    },
    raw_content: ingestion.raw_content,
    raw_html: ingestion.raw_html ?? undefined,
    scraped_at: ingestion.scraped_at ?? undefined,
    scrape_error: ingestion.scrape_error ?? undefined,
    scrape_options: ingestion.scrape_options ?? undefined,
    status: ingestion.status,
    data_type: ingestion.data_type,
    structured_extraction: null,
    ai_notes: null,
  };
}
